import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColumnResizeParams, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import { DataGridPremium, GridFilterItem, GridFilterModel, GridRowParams, GridRowSelectionModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { format } from 'date-fns';
import { Package } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShipmentDetail } from 'src/components/shipment/ShipmentDetail';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import {
  ShipmentOrderAggregate,
  ShipmentOrderSummary,
} from 'src/entities/ShipmentOrderList.entity';
import { useComposition } from 'src/hooks/useComposition';
import { useMutationShipmentOrder, useQueryShipmentOrders } from 'src/hooks/useQueryShipmentOrders';
import { useQueryShipmentOrdersSummaryByShipper } from 'src/hooks/useQueryShipmentOrdersSummaryByShipper';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import { debounce } from 'src/utils/debounce';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { SelectAllSnackbars } from '../common/SelectAllSnackbars';

import {
  buildShipperSummaryPrintHtml,
  formatSummaryValue,
  ShipperSummaryItem,
} from './buildShipperSummaryPrintHtml';
import { createShipmentOrderColumns, ShipmentOrderGridRow } from './Columns';
import ShipmentOrdersToolbar, { CustomToolbarProps } from './CustomToolbar';
import PdfSettingDialog from './PdfSettingDialog';

interface Props {
  startDate: string;
  endDate: string;
  isPlanningView?: boolean;
  setOrderGridApiRef?: (apiRef: React.MutableRefObject<GridApiPremium>) => void;
}

const toGridRow = (shipment: ShipmentOrderSummary): ShipmentOrderGridRow => ({
  id: shipment.id,
  code: shipment.code || String(shipment.id),
  shipper_name: shipment.shipper?.name ?? '--',
  loading_name: shipment.pickup.location?.name ?? '--',
  loading_address: shipment.pickup.location?.address ?? '--',
  relay_location_name: shipment.relay?.location?.name ?? '--',
  relay_location_address: shipment.relay?.location?.address ?? '--',
  unloading_name: shipment.delivery.location?.name ?? '--',
  unloading_address: shipment.delivery.location?.address ?? '--',
  pickup_date: shipment.pickup.date ?? null,
  delivery_date: shipment.delivery.date ?? null,
  product_count: shipment.cargo.product_count ?? null,
  quantity: shipment.cargo.quantity ?? null,
  weight_kg: shipment.cargo.weight_kg ?? null,
  volume_m3: shipment.cargo.volume_m3 ?? null,
  billing_amount_total: shipment.billing?.total_amount ?? 0,
  registration_user: shipment.registration.user_name ?? '--',
  registration_datetime: shipment.registration.recorded_at ?? null,
  updated_datetime: shipment.updated_at ?? shipment.registration.updated_at ?? null,
  status_label: shipment.status_label,
});

const V2ShipmentOrdersPresenter: FC<Props> = memo(
  ({
    startDate,
    endDate,
    isPlanningView,
    setOrderGridApiRef,
  }) => {
    const apiRef = useGridApiRef();

    const licenseContext = useContext(LicenseContext);
    const plansContext = useContext(PlansContext);
    const { enqueueSnackbar } = useSnackbar();
    // 日本語入力の変換中かどうかを管理するフック
    const { isComposing, onCompositionStart, onCompositionEnd } = useComposition();

    const localStorageItemPrefix = `${isPlanningView ? 'map-' : ''}shipment-orders-columns`;
    const paginationStorageKey = `${localStorageItemPrefix}-pagination`;
    const densityStorageKey = `${localStorageItemPrefix}-density`;

    const [startOn, setStartOn] = useState<Date | undefined>(() => (startDate ? new Date(startDate) : undefined));
    const [endOn, setEndOn] = useState<Date | undefined>(() => (endDate ? new Date(endDate) : undefined));
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
    const [paginationModel, setPaginationModel] = useState(() => {
      const savedPagination = localStorage.getItem(paginationStorageKey);
      if (savedPagination) {
        try {
          const parsed = JSON.parse(savedPagination) as unknown as { page: number; pageSize: number };
          return { page: 0, pageSize: parsed.pageSize || 25 };
        } catch {
          return { page: 0, pageSize: 25 };
        }
      }
      return { page: 0, pageSize: 25 };
    });
    const [density, setDensity] = useState<'comfortable' | 'standard' | 'compact'>(() => {
      const savedDensity = localStorage.getItem(densityStorageKey);
      if (savedDensity === 'comfortable' || savedDensity === 'standard' || savedDensity === 'compact') {
        return savedDensity;
      }
      return 'standard';
    });
    const [showShipperSummary, setShowShipperSummary] = useState(false);
    const [drawerOrderId, setDrawerOrderId] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<(number)[]>([]);
    const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [canDeleteAll, _setCanDeleteAll] = useState(false);
    const [globalSearchText, setGlobalSearchText] = useState('');
    const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
    const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [sortCondition, setSortCondition] = useState('');
    const [filterQuery, setFilterQuery] = useState('');
    const [downloadOutsourcingPdfSettingsIsOpen, setDownloadOutsourcingPdfSettingsIsOpen] = useState(false);

    const handlePaginationModelChange = useCallback((newPaginationModel: { page: number; pageSize: number }) => {
      setPaginationModel(newPaginationModel);

      // pageSizeが変更された場合のみlocalStorageに保存
      if (newPaginationModel.pageSize !== paginationModel.pageSize) {
        localStorage.setItem(paginationStorageKey, JSON.stringify({
          pageSize: newPaginationModel.pageSize
        }));
      }
    }, [paginationModel.pageSize, paginationStorageKey]);

    const handleDensityChange = useCallback((newDensity: 'comfortable' | 'standard' | 'compact') => {
      setDensity(newDensity);

      localStorage.setItem(densityStorageKey, newDensity);
    }, []);

    useEffect(() => {
      setStartOn(new Date(startDate));
      setEndOn(new Date(endDate));

      plansContext.setStartOn(startDate);
      plansContext.setEndOn(endDate);
      licenseContext.setStartEndOn({ startOn: startDate, endOn: endDate });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- コンテキスト参照を依存に含めると無限ループになるため
    }, [endDate, startDate]);

    useEffect(() => {
      if (!setOrderGridApiRef) return;
      setOrderGridApiRef(apiRef);
    }, [apiRef, setOrderGridApiRef]);

    useEffect(() => {
      const savedVisibility = dataGridUtil.getSavedColumnVisibilities(localStorageItemPrefix);
      if (savedVisibility && Object.keys(savedVisibility).length > 0) {
        setColumnVisibilityModel(savedVisibility);
      }
    }, [localStorageItemPrefix]);

    const startOnParam = startOn ? format(startOn, 'yyyy-MM-dd') : undefined;
    const endOnParam = endOn ? format(endOn, 'yyyy-MM-dd') : undefined;

    const shipmentOrdersQuery = useQueryShipmentOrders({
      page: paginationModel.page + 1,
      perPage: paginationModel.pageSize,
      startOn: startOnParam,
      endOn: endOnParam,
      search: globalSearchText || undefined,
      filters: filterQuery || undefined,
      sortCondition: sortCondition || undefined,
    });

    // ダイアログ表示時のみ荷主別集計APIを呼び出し、不要な通信を抑制する
    const shipmentOrdersSummaryByShipperQuery = useQueryShipmentOrdersSummaryByShipper({
      startOn: startOnParam,
      endOn: endOnParam,
      enabled: showShipperSummary,
    });

    const { bulkDelete } = useMutationShipmentOrder();

    const shipments = useMemo(() => shipmentOrdersQuery.data?.data ?? [], [shipmentOrdersQuery.data]);

    const rows: ShipmentOrderGridRow[] = useMemo(
      () => shipments.map(toGridRow),
      [shipments]
    );

    const metaData = shipmentOrdersQuery?.data?.meta;
    const rowCount = metaData?.total_count ?? rows.length;
    const summary: ShipmentOrderAggregate | undefined = metaData?.summary;

    const summaryStats = useMemo(() => {
      if (summary) {
        return {
          totalShipments: summary.total_shipments,
          totalQuantity: summary.total_quantity,
          totalWeightDisplay: formatSummaryValue(summary.total_weight_kg, 'kg'),
          totalVolumeDisplay: formatSummaryValue(summary.total_volume_m3, '㎥'),
        };
      }

      const totals = shipments.reduce(
        (acc, shipment) => {
          acc.totalShipments += 1;
          acc.totalQuantity += shipment.cargo.quantity ?? 0;
          acc.totalWeight += shipment.cargo.weight_kg ?? 0;
          acc.totalVolume += shipment.cargo.volume_m3 ?? 0;
          return acc;
        },
        { totalShipments: 0, totalQuantity: 0, totalWeight: 0, totalVolume: 0 }
      );
      return {
        totalShipments: totals.totalShipments,
        totalQuantity: totals.totalQuantity,
        totalWeightDisplay: formatSummaryValue(totals.totalWeight, 'kg'),
        totalVolumeDisplay: formatSummaryValue(totals.totalVolume, '㎥'),
      };
    }, [summary, shipments]);

    const shipperSummary: ShipperSummaryItem[] = useMemo(() => {
      // APIレスポンス（キャメルケース）をUI表示用に整形し、小数値は数値へ変換してフォーマット処理を簡潔にする
      const entries = shipmentOrdersSummaryByShipperQuery.data?.data;
      if (!entries) return [];

      return entries.map((item) => ({
        shipperName: item.shipperName,
        totalShipments: item.shipmentOrderCount,
        deliveryCount: item.deliveryCount ?? 0,
        totalQuantity: item.quantityTotal,
        totalWeight: Number(item.weightTotal ?? 0),
        totalVolume: Number(item.volumeTotal ?? 0),
      }));
    }, [shipmentOrdersSummaryByShipperQuery.data]);

    const shipperTotals = useMemo(() => {
      const totals = shipmentOrdersSummaryByShipperQuery.data?.totals;
      if (!totals) return undefined;

      return {
        shipmentOrderCount: totals.shipmentOrderCount,
        deliveryCount: totals.deliveryCount ?? 0,
        quantityTotal: totals.quantityTotal,
        totalWeightKg: Number(totals.weightTotal ?? 0),
        totalVolumeM3: Number(totals.volumeTotal ?? 0),
        assignedVehicleCount: totals.assignedVehicleCount,
      };
    }, [shipmentOrdersSummaryByShipperQuery.data]);

    const drawerOrder = useMemo(() => {
      if (drawerOrderId === null) return null;
      return shipments.find((shipment) => String(shipment.id) === String(drawerOrderId)) ?? null;
    }, [drawerOrderId, shipments]);

    const isShipperSummaryReady = !shipmentOrdersSummaryByShipperQuery.isLoading
      && !shipmentOrdersSummaryByShipperQuery.isError;

    const printTooltipTitle = (() => {
      if (isShipperSummaryReady) return '印刷';
      if (shipmentOrdersSummaryByShipperQuery.isError) return 'エラーのため印刷できません';
      return '読み込み中は印刷できません';
    })();

    const handlePrintShipperSummary = useCallback(() => {
      if (!isShipperSummaryReady) return;

      const html = buildShipperSummaryPrintHtml(shipperSummary, {
        startDate,
        endDate,
        totals: shipperTotals,
      });
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        enqueueSnackbar('印刷ウィンドウを開けませんでした。ブラウザのポップアップブロックを解除してください。', { variant: 'warning' });
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    }, [isShipperSummaryReady, shipperSummary, shipperTotals, startDate, endDate, enqueueSnackbar]);

    const columns = useMemo(
      () => createShipmentOrderColumns(localStorageItemPrefix),
      [localStorageItemPrefix]
    );

    const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(model);
      dataGridUtil.setSavedColumnVisibilities(localStorageItemPrefix, model);
    };

    const handleColumnWidthChange = (params: GridColumnResizeParams) => {
      dataGridUtil.setSavedColumnWidth(localStorageItemPrefix, params);
    };

    const filterSearch = (filterModel: GridFilterModel) => {
      const items = filterModel?.items?.filter((item) => {
        const operator = item.operator || '';
        const allowsEmptyValue = ['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'].includes(operator);
        if (allowsEmptyValue) return true;
        if (item.value === undefined || item.value === null) return false;
        if (Array.isArray(item.value)) return item.value.length > 0;
        return item.value !== '';
      });

      if (!items || items.length === 0) {
        setOrConditions([]);
        setAndConditions([]);
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        return;
      }

      const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => (
        operatorValueUtil.convertToRansackPredicate(
          stringUtil.toSnakeCase(item.field || ''),
          item.operator || '',
          item.value as string | string[] | undefined,
          filterModel.logicOperator || 'and'
        )
      ));

      setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
      setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    // 変換中にスキップされたフィルターモデルを保持
    const pendingFilterModelRef = useRef<GridFilterModel | null>(null);
    const debouncedFilterSearch = useRef(debounce(filterSearch, 300)).current;

    const handleFilterModelChange = (filterModel: GridFilterModel) => {
      if (isComposing) {
        pendingFilterModelRef.current = filterModel;
        return;
      }

      if (debouncedFilterSearch && typeof debouncedFilterSearch.cancel === 'function') {
        debouncedFilterSearch.cancel();
      }
      debouncedFilterSearch(filterModel);
      pendingFilterModelRef.current = null;
    };

    const handleSortModelChange = (nextSortModel: GridSortModel) => {
      setSortModel(nextSortModel);

      if (nextSortModel.length === 0) {
        setSortCondition('');
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        return;
      }

      const [{ field, sort }] = nextSortModel;
      if (!field || !sort) {
        setSortCondition('');
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        return;
      }

      setSortCondition([
        stringUtil.toSnakeCase(field),
        sort,
      ].join('+'));
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    };

    // 日本語入力で保留されたフィルターがあれば実行する
    useEffect(() => {
      if (!isComposing && pendingFilterModelRef.current) {
        const pendingModel = pendingFilterModelRef.current;
        pendingFilterModelRef.current = null;

        // debounce処理を実行
        if (debouncedFilterSearch && typeof debouncedFilterSearch.cancel === 'function') {
          debouncedFilterSearch.cancel();
        }
        debouncedFilterSearch(pendingModel);
      }
    }, [debouncedFilterSearch, isComposing]);

    // DataGridでの選択変更をselectedIdsに同期させる
    const handleRowSelectionModelChange = useCallback((rowSelectionModel: GridRowSelectionModel) => {
      const ids = Array.from(rowSelectionModel.ids).map((id) => Number(id)); // DataGridの選択IDを数値配列に変換する

      if (rowSelectionModel.type === 'include') { // includeモードで選択されたID集合かを判定する
        setSelectedIds(ids); // includeモードでは受け取ったIDをそのまま保存する
        return; // exclude判定へは進まず即時終了する
      }

      const allIds = shipments.map((shipment) => Number(shipment.id)); // 取得済みデータから全行IDを抽出する
      setSelectedIds(allIds.filter((id) => !ids.includes(id))); // excludeモードでは除外IDを差し引いた残りを選択状態として保持する
    }, [shipments]);

    const removeSelectedOrders = () => {
      if (!selectedIds.length) return Promise.resolve();

      if (!window.confirm(`${selectedIds.length}件の伝票を削除します。よろしいですか?`)) return Promise.resolve();

      return new Promise<void>((resolve) => {
        bulkDelete.mutate(selectedIds, {
          onSuccess: () => {
            setSelectedIds([]);
          },
          onSettled: () => {
            resolve();
          }
        });
      });
    }

    const toggleDialogIsOpen = () => {
      setNewShipmentDialogOpen((prev) => !prev);
    };

    const deleteAll = (_settledFnk: () => void) => {
      // TODO: implement deletion logic
    };

    const buildExportSearchParams = () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(paginationModel.page + 1));
      searchParams.set('per', String(paginationModel.pageSize));
      if (startOnParam) searchParams.set('start_on', startOnParam);
      if (endOnParam) searchParams.set('end_on', endOnParam);
      if (globalSearchText) searchParams.set('keywords', globalSearchText);
      if (filterQuery) {
        const filterParams = new URLSearchParams(filterQuery);
        filterParams.forEach((value, key) => {
          searchParams.append(key, value);
        });
      }
      if (sortCondition) searchParams.append('sort_condition', sortCondition);
      if (selectedIds.length > 0) {
        searchParams.set('ids', selectedIds.join(','));
      }
      return searchParams;
    };

    const handleDownloadExcel = (version?: string) => {
      const searchParams = buildExportSearchParams();
      if (version) searchParams.set('version', version);

      const requestUrl = `/api/v5/shipment_orders/export?${searchParams.toString()}`;
      window.open(requestUrl, '_blank', 'noopener');
    };

    const handleDownloadCsv = () => {
      const searchParams = buildExportSearchParams();
      searchParams.set('export_format', 'csv');

      const requestUrl = `/api/v5/shipment_orders/export?${searchParams.toString()}`;
      window.open(requestUrl, '_blank', 'noopener');
    };

    const onGlobalSearchChange = (value: string) => {
      setGlobalSearchText(value);
    };

    const handlePdfSettingsClose = useCallback(() => {
      setDownloadOutsourcingPdfSettingsIsOpen(false);
    }, []);

    const onClickDownloadOutsourcingPdf = () => {
      if (selectedIds.length === 0) {
        enqueueSnackbar('出力する案件を選択してください。');
        return;
      }
      setDownloadOutsourcingPdfSettingsIsOpen(true);
    };

    useEffect(() => {
      const nextFilterQuery = [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
      ].filter((maybe) => maybe).join('&');

      setFilterQuery(nextFilterQuery);
    }, [andConditions, orConditions]);

    return (
      <>
        <Helmet>
          <title>案件管理</title>
        </Helmet>
        <Box
          sx={{
            bgcolor: 'background.paper',
            height: `calc(100vh - ${appBarHeight}px)`, // appBarHeightを考慮
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            overflow: 'hidden',
          }}
        >
          <SelectAllSnackbars
            totalPageCount={metaData?.total_page || 1}
            totalCount={metaData?.total_count || 0}
            perPage={metaData?.per_page}
            allIds={metaData?.all_ids || []}
            selectedIds={selectedIds}
            apiRef={apiRef}
            setSelectedIds={setSelectedIds}
          />
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: 1,
              mb: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-between', alignItems: 'center', }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Package size={20} color="white" />
                  </Box>
                  <Box>
                    <Typography variant="h3" fontWeight="bold">
                      案件
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, 140px)' },
                  gridAutoRows: '70px',
                }}
              >
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowShipperSummary(true)}
                  sx={{ textDecoration: 'underline', wordBreak: 'keep-all' }}
                >
                  荷主別に詳細表示
                </Button>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {summaryStats.totalShipments.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      伝票数
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {summaryStats.totalQuantity.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      数量合計
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {summaryStats.totalWeightDisplay}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      重量合計
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {summaryStats.totalVolumeDisplay}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      体積合計
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
            <Paper
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
              }}
            >
              <DataGridPremium
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                loading={shipmentOrdersQuery.isLoading || shipmentOrdersQuery.isFetching || bulkDelete.isPending}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                filterMode="server"
                rowCount={rowCount}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                onColumnWidthChange={handleColumnWidthChange}
                onFilterModelChange={handleFilterModelChange}
                disableRowSelectionOnClick
                checkboxSelection
                onRowSelectionModelChange={handleRowSelectionModelChange}
                onRowClick={(params: GridRowParams) => {
                  setDrawerOrderId(Number(params.id));
                }}
                density={density}
                onDensityChange={handleDensityChange}
                slots={{
                  loadingOverlay: CustomLoadingOverlay,
                  toolbar: ShipmentOrdersToolbar,
                }}
                slotProps={{
                  toolbar: {
                    startOn,
                    endOn,
                    removeSelectedOrders,
                    toggleDialogIsOpen,
                    setIsLoading,
                    isLoading,
                    canDeleteAll,
                    deleteAll,
                    handleDownloadExcel,
                    handleDownloadCsv,
                    globalSearchText,
                    onGlobalSearchChange,
                    onClickDownloadOutsourcingPdf,
                  } as Partial<CustomToolbarProps>,
                  baseTextField: {
                    onCompositionStart,
                    onCompositionEnd,
                  }
                }}
                showToolbar
                pagination
                disableAggregation
                disablePivoting
                disableRowGrouping
                sx={{
                  flex: 1, // DataGrid自体も残りの高さを使用
                  height: '100%', // 親要素の高さを100%使用
                  '& .MuiDataGrid-virtualScroller': {
                    // DataGrid内部のスクロール領域を制御
                    overflow: 'auto',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer', // 行にマウスオーバーした時のカーソルをポインターに設定
                  },
                }}
              />
            </Paper>
          </Container>
        </Box>

        {downloadOutsourcingPdfSettingsIsOpen && selectedIds.length > 0 && (
          <PdfSettingDialog
            shipmentOrderIds={selectedIds}
            downloadOutsourcingPdfSettingsIsOpen={downloadOutsourcingPdfSettingsIsOpen}
            handlePdfSettingsClose={handlePdfSettingsClose}
          />
        )}

        {/* New Shipment Dialog */}
        {newShipmentDialogOpen && (
          <ShipmentOrderForm
            baseDate={startOn}
            startOn={startDate}
            newShipmentDialogOpen={newShipmentDialogOpen}
            setNewShipmentDialogOpen={setNewShipmentDialogOpen}
          />
        )}

        {drawerOrder && (
          <ShipmentDetail
            shipmentId={drawerOrderId}
            editShipmentDialogOpen={Boolean(drawerOrderId)}
            onClose={() => setDrawerOrderId(null)}
            startOn={startDate}
          />
        )}

        <Dialog
          open={showShipperSummary}
          onClose={() => setShowShipperSummary(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">荷主別集計</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={printTooltipTitle}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={handlePrintShipperSummary}
                      disabled={!isShipperSummaryReady}
                    >
                      印刷
                    </Button>
                  </span>
                </Tooltip>
                <IconButton onClick={() => setShowShipperSummary(false)} aria-label="閉じる">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          </DialogTitle>
          <DialogContent>
            {shipperTotals && (() => {
              const totalsItems = [
                { label: '伝票数合計', value: `${shipperTotals.shipmentOrderCount.toLocaleString()} 件`, color: '#1976d2' },
                { label: '納品件数合計', value: `${shipperTotals.deliveryCount.toLocaleString()} 件`, color: '#1976d2' },
                { label: '数量合計', value: shipperTotals.quantityTotal.toLocaleString(), color: '#1976d2' },
                { label: '重量合計', value: `${shipperTotals.totalWeightKg.toLocaleString()} kg`, color: '#1976d2' },
                { label: '体積合計', value: `${shipperTotals.totalVolumeM3.toLocaleString()} ㎥`, color: '#1976d2' },
                { label: '配車済み車両台数', value: `${shipperTotals.assignedVehicleCount.toLocaleString()} 台`, color: '#ea580c' },
              ];
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 1.5,
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {totalsItems.map((it) => (
                    <Box key={it.label} sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mb: 0.3 }}>
                        {it.label}
                      </Typography>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: it.color }}>
                        {it.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>荷主名</TableCell>
                  <TableCell align="right">伝票数</TableCell>
                  <TableCell align="right">納品件数</TableCell>
                  <TableCell align="right">数量合計</TableCell>
                  <TableCell align="right">重量合計</TableCell>
                  <TableCell align="right">体積合計</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentOrdersSummaryByShipperQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">荷主別集計を取得しています...</TableCell>
                  </TableRow>
                )}
                {shipmentOrdersSummaryByShipperQuery.isError && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">荷主別集計の取得に失敗しました。</TableCell>
                  </TableRow>
                )}
                {!shipmentOrdersSummaryByShipperQuery.isLoading
                  && !shipmentOrdersSummaryByShipperQuery.isError
                  && shipperSummary.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">表示できる集計がありません。</TableCell>
                    </TableRow>
                )}
                {shipperSummary.map((item) => (
                  <TableRow key={item.shipperName}>
                    <TableCell>{item.shipperName}</TableCell>
                    <TableCell align="right">{item.totalShipments.toLocaleString()}</TableCell>
                    <TableCell align="right">{item.deliveryCount.toLocaleString()}</TableCell>
                    <TableCell align="right">{item.totalQuantity.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatSummaryValue(item.totalWeight, 'kg')}</TableCell>
                    <TableCell align="right">{formatSummaryValue(item.totalVolume, '㎥')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowShipperSummary(false)}>閉じる</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

V2ShipmentOrdersPresenter.displayName = 'V2ShipmentOrdersPresenter';

export default V2ShipmentOrdersPresenter;
